// A person who signs in on behalf of a partner.
//
// One doc per account, keyed by the Firebase Auth uid, created when an
// invitation is redeemed (see types_invite.ts). A uid belongs to exactly one
// partner — an account cannot be shared across partners.
//
// Docs are written only by Cloud Functions; the client reads them.

// Firestore collection name.
export const coll_partnerUsers = 'partnerUsers';

// 'owner'  — the partner's manager: maintains the partner profile and invites
//            staff. A partner has at most one, held via `partners/{id}.ownerUid`.
// 'member' — a staff account: makes booking requests, but cannot edit the
//            partner profile or invite anyone.
//
// The stored values stay 'owner' / 'member'; `partnerRoleLabels` is the only
// place the human names live, so renaming them is copy, not a migration.
export type PartnerRoleType = 'owner' | 'member';

export const partnerRoleLabels: Record<PartnerRoleType, string> = {
  owner: 'Manager',
  member: 'Staff',
};

export type PartnerUserStatusType = 'active' | 'disabled';

export interface PartnerUserType {
  id: string; // === Firebase Auth uid
  partnerId: string;
  role: PartnerRoleType;

  // Snapshot of the account at redemption time, for display in admin lists
  // without a second lookup against Firebase Auth.
  email: string; // lowercased
  displayName: string;

  status: PartnerUserStatusType;

  // Who issued the invitation they redeemed: an Eighty Days admin for the
  // partner's first manager, that manager for staff invites.
  invitedByUid?: string;
  invitedByInviteId: string;

  createdAt: string;
  updatedAt: string;
}

/**
 * The custom claims a partner user carries on their ID token. Security rules
 * read these instead of loading `partnerUsers/{uid}`, so partner checks cost no
 * document reads. Kept in sync by the `partnerUsers_written` Cloud Function.
 */
export interface PartnerClaimsType {
  partnerId: string;
  partnerRole: PartnerRoleType;
}

// --- Account management callables ---
//
// `partnerUsers` is closed to client writes, so every change below goes through
// a Cloud Function. Who may call them: an Eighty Days admin for any account, or
// a partner's own manager for the staff at that partner. Role changes are
// admin-only — a partner cannot promote anyone to its own level.

export interface SetPartnerUserStatusRequestType {
  uid: string;
  status: PartnerUserStatusType;
}

export interface SetPartnerUserRoleRequestType {
  uid: string;
  role: PartnerRoleType;
}

export interface DeletePartnerUserRequestType {
  uid: string;
}
